import { takeEvery, put, call, all, fork, select } from "@redux-saga/core/effects";
import { addErrorLog } from "../../../Logger/logger";
import {
   FETCH_CALENDAR_EVENTS,
   FETCH_CALENDAR_FLOWS,
   PAUSE_SCHEDULE,
   RESUME_SCHEDULE,
   CREATE_SCHEDULE,
   UPDATE_SCHEDULE,
   DELETE_SCHEDULE,
   SET_SCHEDULED_FLOW_FLAG,
   FETCH_CALENDAR_EVENTS_BY_FLOW_ID,
   CREATE_SCHEDULE_FROM_TRIGGER,
   UPDATE_SCHEDULE_FROM_TRIGGER,
   UPDATE_SCHEDULE_DOCUMENTATION,
   FETCH_SHARED_SCHEDULES
} from "redux/constants/flowDesigner";
import { fetchSchedule, fetchFlows, updateScheduleStatus, createSchedule, updateSchedule, deleteSchedule, updateScheduleDocumentation, fetchSharedSchedules } from "api/FlowDesignerApis";
import { getResourceDependencies } from "api/ResourceDependenciesApi";
import { setCalendarEvents, setCalendarFlows, setSaveScheduleFailedReset, setSaveScheduleSuccess, fetchCalendarEventsByFlowId, setSharedSchedules } from "redux/actions/flowDesigner/calendarActionCreator";
import { updateFlowTrigger, clearSelectedTrigger } from "redux/actions/flowDesigner/triggersActionCreator";
import { setSelectedSchedule } from "redux/actions/flowDesigner/triggersActionCreator";
import { mapScheduleToEvent } from "helpers/mappers";
import { hideContextPanel } from "redux/actions/flowDesigner/editorContextActionCreator";
import { openTriggerPropertiesPanel } from "redux/actions/flowDesigner/triggersActionCreator";
import { toast } from "react-toastify";

/***** WORKERS *****/

function* handleFetchFlows({ payload }) {
   try {
      const { tenant_id } = payload;
      const res = yield call(fetchFlows, tenant_id);

      if (res) {
         const newFlows = res.map((r) => {
            return {
               value: r.id,
               label: r.name
            };
         });
         yield put(setCalendarFlows(newFlows));
      }
   } catch (error) {
      addErrorLog("handleFetchFlows", error);
   }
}

function* fetchCalendarEvents(tenant_id) {
   try {
      const res = yield call(fetchSchedule, tenant_id);

      if (res) {
         const events = res.map((r) => {
            const event = mapScheduleToEvent(r);

            if (r.repeat_type === "once_a_week" || r.repeat_type === "every_day") {
               event.startRecur = r.start_date;

               if (r.repeat_type === "once_a_week") {
                  event.daysOfWeek = [new Date(r.start_date).getDay()];
               }
            }
            return event;
         });

         yield put(setCalendarEvents(events));
      }
   } catch (error) {
      addErrorLog("fetchCalendarEvents", error);
   }
}

function* handleFetchCalendarEvents({ payload }) {
   try {
      const { tenant_id } = payload;
      yield fetchCalendarEvents(tenant_id);
   } catch (error) {
      addErrorLog("handleFetchCalendarEvents", error);
   }
}

function* handlePauseResumeSchedule({ payload }) {
   try {
      const { tenant_id, schedule_id, status } = payload;
      yield call(updateScheduleStatus, tenant_id, schedule_id, status || "Paused");
   } catch (error) {
      addErrorLog("handleFetchCalendarEvents", error);
   }
}

function* saveNewSchedule(payload, isTrigger) {
   try {
      const { tenant_id, input } = payload;
      yield put(setSaveScheduleFailedReset());

      if (isTrigger) {
         const { selectedTrigger } = yield select((state) => state?.designer?.triggers);
         const inputCopy = { ...input };
         const updatedTrigger = { ...selectedTrigger, properties: inputCopy };
         delete input.notes;

         const res = yield call(createSchedule, tenant_id, input);

         if (res) {
            const schedule_id = res.id;
            updatedTrigger.properties.schedule_id = schedule_id;
            yield put(updateFlowTrigger(updatedTrigger));
            yield put(clearSelectedTrigger());
         }
      } else {
         const createdSchedule = yield call(createSchedule, tenant_id, input);

         if (window.location.href.includes("designer")) {
            yield put(hideContextPanel());
            yield put(openTriggerPropertiesPanel());
            yield put(setSelectedSchedule(mapScheduleToEvent(createdSchedule)));
         }
      }

      yield fetchCalendarEvents(tenant_id);
      yield put(setSaveScheduleSuccess());
      yield put(fetchCalendarEventsByFlowId(tenant_id, input.flow_id));
   } catch (error) {
      addErrorLog("saveNewSchedule", error);
   }
}

function* handleCreateSchedule({ payload }) {
   try {
      yield saveNewSchedule(payload, false);
   } catch (error) {
      addErrorLog("handleCreateSchedule", error);
   }
}

function* handleCreateScheduleFromTrigger({ payload }) {
   try {
      yield saveNewSchedule(payload, true);
   } catch (error) {
      addErrorLog("handleCreateScheduleFromTrigger", error);
   }
}

function* handleUpdateSchedule({ payload }) {
   try {
      const { tenant_id, schedule_id, input } = payload;

      yield put(setSaveScheduleFailedReset());
      yield call(updateSchedule, tenant_id, schedule_id, input);
      yield put(setSaveScheduleSuccess());
      yield put(fetchCalendarEventsByFlowId(tenant_id, input.flow_id));
   } catch (error) {
      addErrorLog("handleUpdateSchedule", error);
   }
}

function* handleUpdateScheduleFromTrigger({ payload }) {
   try {
      const { selectedTrigger } = yield select((state) => state?.designer?.triggers);
      const { input, schedule_id } = payload;
      const inputCopy = { ...input };
      const updatedTrigger = { ...selectedTrigger, properties: inputCopy };
      updatedTrigger.properties.schedule_id = schedule_id;

      yield handleUpdateSchedule({ payload });

      yield put(updateFlowTrigger(updatedTrigger));
      yield put(clearSelectedTrigger());
   } catch (error) {
      addErrorLog("handleUpdateScheduleFromTrigger", error);
   }
}

function* handleDeleteSchedule({ payload }) {
   try {
      const { tenant_id, schedule_id } = payload;
      const { currentSavedFlow } = yield select((state) => state?.designer?.flows);
      const response = yield call(deleteSchedule, tenant_id, schedule_id);

      if (!response?.isSuccessful) {
         toast.error(response?.message || "Failed to remove schedule");
         return;
      }

      toast.success("Schedule successfully removed.");
      yield put(fetchCalendarEventsByFlowId(tenant_id, currentSavedFlow.id));
   } catch (error) {
      addErrorLog("handleDeleteSchedule", error);
   }
}

function* handleFetchScheduleByTenantAndFlowId({ payload }) {
   try {
      const { tenant_id, flow_id } = payload;
      if (flow_id) {
         const schedulesByFlowId = yield call(getResourceDependencies, {
            tenantId: tenant_id,
            sourceId: flow_id,
            direction: "reverse",
            targetResourceType: "schedule"
         });
         yield put({
            type: SET_SCHEDULED_FLOW_FLAG,
            payload: !!schedulesByFlowId?.resultObject?.length
         });
      }
   } catch (error) {
      addErrorLog("handleFetchScheduleByTenantAndFlowId", error);
   }
}

function* handleUpdateScheduleDocumentation({ payload }) {
   try {
      const { tenant_id, id, documentation } = payload;
      const { calendarEvents } = yield select((state) => state.designer.calendar);

      const res = yield call(updateScheduleDocumentation, tenant_id, id, documentation);

      if (res) {
         yield put(setCalendarEvents(calendarEvents.map((event) => (event.id === id ? { ...event, documentation } : event))));
      }
   } catch (error) {
      addErrorLog("handleUpdateScheduleDocumentation", error);
   }
}

function* handleFetchSharedSchedules({ payload }) {
   try {
      const { tenant_id } = payload;

      const res = yield call(fetchSharedSchedules, tenant_id);

      if (res) {
         yield put(setSharedSchedules(res));
      }
   } catch (error) {
      addErrorLog("handleFetchSharedSchedules", error);
   }
}

/***** WATCHERS *****/

function* watchFetchFlows() {
   yield takeEvery(FETCH_CALENDAR_FLOWS, handleFetchFlows);
}
function* watchFetchCalendarEvents() {
   yield takeEvery(FETCH_CALENDAR_EVENTS, handleFetchCalendarEvents);
}
function* watchPauseSchedule() {
   yield takeEvery(PAUSE_SCHEDULE, handlePauseResumeSchedule);
}
function* watchResumeSchedule() {
   yield takeEvery(RESUME_SCHEDULE, handlePauseResumeSchedule);
}
function* watchCreateSchedule() {
   yield takeEvery(CREATE_SCHEDULE, handleCreateSchedule);
}
function* watchCreateScheduleFromTrigger() {
   yield takeEvery(CREATE_SCHEDULE_FROM_TRIGGER, handleCreateScheduleFromTrigger);
}
function* watchUpdateScheduleFromTrigger() {
   yield takeEvery(UPDATE_SCHEDULE_FROM_TRIGGER, handleUpdateScheduleFromTrigger);
}
function* watchUpdateSchedule() {
   yield takeEvery(UPDATE_SCHEDULE, handleUpdateSchedule);
}
function* watchDeleteSchedule() {
   yield takeEvery(DELETE_SCHEDULE, handleDeleteSchedule);
}
function* watchFetchScheduleByTenantAndFlowId() {
   yield takeEvery(FETCH_CALENDAR_EVENTS_BY_FLOW_ID, handleFetchScheduleByTenantAndFlowId);
}
function* watchUpdateScheduleDocumentation() {
   yield takeEvery(UPDATE_SCHEDULE_DOCUMENTATION, handleUpdateScheduleDocumentation);
}
function* watchFetchSharedSchedules() {
   yield takeEvery(FETCH_SHARED_SCHEDULES, handleFetchSharedSchedules);
}

export function* rootCalendarSaga() {
   yield all([
      fork(watchFetchCalendarEvents),
      fork(watchFetchFlows),
      fork(watchPauseSchedule),
      fork(watchResumeSchedule),
      fork(watchCreateSchedule),
      fork(watchUpdateSchedule),
      fork(watchDeleteSchedule),
      fork(watchFetchScheduleByTenantAndFlowId),
      fork(watchCreateScheduleFromTrigger),
      fork(watchUpdateScheduleFromTrigger),
      fork(watchUpdateScheduleDocumentation),
      fork(watchFetchSharedSchedules)
   ]);
}
